/**
 * Components for sections/product-hero-buy.liquid.
 */

/**
 * <product-hero-slider>
 * Image slider with dots; since 2026-10-10 optional prev/next arrows (shown on
 * desktop only via CSS, buttons [data-phb-prev]/[data-phb-next]). Relies on native
 * horizontal scroll-snap for touch/swipe - this class only keeps the dot
 * indicators in sync with the visible slide and lets a dot click scroll to
 * its slide. Deliberately not built on the theme's generic gridy-slider
 * (assets/global.js, class GridySlider) - that component targets multi-
 * column/thumbnail grids and needs a wider config object than this simple
 * single-row PDP gallery requires. Same "small dedicated custom element
 * per section" pattern as component-product-info-card.js and
 * component-product-comparison.js.
 */
class ProductHeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-phb-track]');
    this.slides = Array.from(this.querySelectorAll('[data-phb-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-phb-dot]'));
    this.prevBtn = this.querySelector('[data-phb-prev]');
    this.nextBtn = this.querySelector('[data-phb-next]');
    if (!this.track || this.slides.length < 2) return;

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.currentIndex() - 1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.currentIndex() + 1));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.currentIndex() - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.currentIndex() + 1); }
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.phbDot, 10);
        this.goTo(index);
      });
    });

    this._onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this._onScroll, { passive: true });
    this.updateActiveDot();
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this._onScroll);
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  }

  currentIndex() {
    const trackLeft = this.track.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Infinity;
    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - trackLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  }

  onScroll() {
    window.clearTimeout(this._scrollTimeout);
    this._scrollTimeout = window.setTimeout(() => this.updateActiveDot(), 80);
  }

  updateActiveDot() {
    const closestIndex = this.currentIndex();
    if (this.prevBtn) this.prevBtn.disabled = closestIndex <= 0;
    if (this.nextBtn) this.nextBtn.disabled = closestIndex >= this.slides.length - 1;
    this.dots.forEach((dot, index) => {
      const isActive = index === closestIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}
customElements.define('product-hero-slider', ProductHeroSlider);

/**
 * <product-delivery-estimate>
 * Fills in the "Voraussichtlich am <Wochentag> in deiner Küche" text.
 * Date/weekday calculation (cutoff 12:00 Uhr, Sonntag + deutsche
 * Feiertage inkl. beweglicher Feiertage per Gauß'scher Osterformel
 * gesperrt, +1 Werktag für die Zustellung) is ported 1:1 from the
 * existing inline script in the Dawn theme's
 * `Shopify Editor/templates/product.json` (custom_liquid block
 * "custom_liquid_BcbTtK" on the main-product section) - see the header
 * comment in sections/product-hero-buy.liquid for the full source
 * reference. Only the JS math was ported as instructed; wording
 * (prefix/suffix) comes from data attributes set by the section's
 * Theme-Editor settings. Runs client-side on purpose (not server-
 * rendered via Liquid 'now'), same as the original, since Shopify pages
 * are CDN-cached and a Liquid-rendered date would reflect the cache
 * generation time, not the visitor's local time.
 */
class ProductDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.textEl = this.querySelector('[data-phb-delivery-text]');
    if (!this.textEl) return;
    const prefix = this.dataset.prefix || 'Voraussichtlich am';
    const suffix = this.dataset.suffix || 'in deiner Küche';
    try {
      this.textEl.textContent = `${prefix} ${this.getDeliveryWeekday()} ${suffix}`;
    } catch (e) {
      console.error('Lieferdatum konnte nicht berechnet werden:', e);
      this.textEl.textContent = 'Lieferdatum derzeit nicht verfügbar';
    }
  }

  getDeliveryWeekday() {
    const now = new Date();
    const hour = now.getHours();
    const fixedHolidays = ['01-01', '05-01', '10-03', '12-25', '12-26'];

    const getEasterSunday = (year) => {
      const f = Math.floor,
        G = year % 19,
        C = f(year / 100),
        H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30,
        I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11)),
        J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7,
        L = I - J,
        month = 3 + f((L + 40) / 44),
        day = L + 28 - 31 * f(month / 4);
      return new Date(year, month - 1, day);
    };

    const getMovableHolidayDates = (year) => {
      const easter = getEasterSunday(year);
      const offsetDays = [-2, 1, 39, 50];
      return offsetDays.map((offset) => {
        const date = new Date(easter);
        date.setDate(easter.getDate() + offset);
        return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      });
    };

    const year = now.getFullYear();
    const holidays = [...fixedHolidays, ...getMovableHolidayDates(year)];

    const isDeliveryBlocked = (date) => {
      const day = date.getDay();
      const mmdd = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      return day === 0 || holidays.includes(mmdd);
    };

    let shippingDate = new Date(now);
    if (hour >= 12) {
      shippingDate.setDate(shippingDate.getDate() + 1);
    }
    while (isDeliveryBlocked(shippingDate)) {
      shippingDate.setDate(shippingDate.getDate() + 1);
    }

    let deliveryDate = new Date(shippingDate);
    let deliveryDaysCounted = 0;
    while (deliveryDaysCounted < 1) {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
      if (!isDeliveryBlocked(deliveryDate)) {
        deliveryDaysCounted++;
      }
    }

    const weekdayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    return weekdayNames[deliveryDate.getDay()];
  }
}
customElements.define('product-delivery-estimate', ProductDeliveryEstimate);
